import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from 'react';
import { useSignals } from '@preact/signals-react/runtime';
import FormModal from 'components/FormModal';
import { Alert, Button, Form, InputNumber, } from 'antd';
import { txt } from 'libs/i18n';
import { useSession } from 'components/Authenticated';
import { getLanguageFromTrsx, recalculateSpeakers, recalculateSpeakersV2 } from 'api/project-api';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { Alerts } from 'components/Alerts';
import styles from './style.module.less';
import { recomputingSpeakersSignal, setRecomputingSpeakersSignal, } from '../../signalsStore/recomputingSpeakers';
// NOTE: Recomputing speakers of a long project can take minutes.
const RECOMPUTE_TIMEOUT_MS = 10 * 60 * 1000;
const SpeakersModal = ({ visible, enqueueProjectUpdate, onSetSpeakersModalVisible, editorController, action, }) => {
    useSignals();
    const [form] = Form.useForm();
    const { session } = useSession();
    const useReclusteringV2 = session.login.hasClaim('reclustering:v2') && action === 'changeSpeakersNumber';
    const [submitError, setSubmitError] = useState(null);
    const applyTrsx = (trsx, project) => {
        const projectLanguage = getLanguageFromTrsx(trsx.content);
        editorController.importTrsx({
            content: trsx.content,
            etag: trsx.etag,
            label: editorController.label,
            language: projectLanguage,
        }, true, project, true);
    };
    const handleSubmit = async (values) => {
        setSubmitError(null);
        await enqueueProjectUpdate(async (initialProject) => {
            const result = await recalculateSpeakers(session.connection, initialProject, action === 'deleteSpeakers' ? 0 : values.speakers);
            if (result.isSuccess()) {
                const { trsx, project } = result.get();
                applyTrsx(trsx, project);
                onSetSpeakersModalVisible(false);
                return project;
            }
            setSubmitError(Alerts.error(txt('error')));
            return initialProject;
        });
    };
    const handleRecompute = async () => {
        setSubmitError(null);
        setRecomputingSpeakersSignal(true);
        try {
            await enqueueProjectUpdate(async (initialProject) => {
                const result = await recalculateSpeakersV2(session.connection, initialProject);
                if (result.isSuccess()) {
                    const { trsx, project } = result.get();
                    applyTrsx(trsx, project);
                    onSetSpeakersModalVisible(false);
                    return project;
                }
                setSubmitError(Alerts.error(txt(result.err() === 'no-voiceprints' ? 'recomputeSpeakersNoVoiceprints' : 'error')));
                return initialProject;
            }, RECOMPUTE_TIMEOUT_MS);
        }
        catch (_a) {
            setSubmitError(Alerts.error(txt('error')));
        }
        finally {
            setRecomputingSpeakersSignal(false);
        }
    };
    if (useReclusteringV2) {
        return (_jsxs(FormModal, { open: visible, onCancel: () => { setSubmitError(null); onSetSpeakersModalVisible(false); }, width: 400, title: txt('recomputeSpeakers'), footer: [
                _jsx(Button, { type: "default", onClick: () => { setSubmitError(null); onSetSpeakersModalVisible(false); }, children: txt('cancel') }),
                _jsx(Button, { type: "primary", loading: recomputingSpeakersSignal.value, onClick: handleRecompute, children: txt('recomputeSpeakers') }, "submit"),
            ], children: [_jsx(Alert, { type: "warning", message: txt('recomputeSpeakersIrreversible'), showIcon: true }), submitError] }));
    }
    return (_jsx(FormModal, { open: visible, onCancel: () => { setSubmitError(null); onSetSpeakersModalVisible(false); }, width: 600, title: action === 'deleteSpeakers'
            ? (_jsxs(_Fragment, { children: [_jsx(ExclamationCircleFilled, {}), ' ', txt('deleteAllSpeakers'), "?"] }))
            : txt('changeSpeakersNumber'), footer: [
            _jsx(Button, { type: "default", onClick: () => { setSubmitError(null); onSetSpeakersModalVisible(false); }, children: txt('cancel') }),
            _jsx(Button, { danger: action === 'deleteSpeakers', type: "primary", htmlType: "submit", onClick: () => form.submit(), children: txt(action === 'deleteSpeakers' ? 'delete' : 'change') }, "submit"),
        ], children: _jsxs("div", { className: styles.container, children: [submitError, _jsx("div", { className: styles.form, children: _jsx(Form, { onFinish: handleSubmit, form: form, initialValues: { speakers: 2 }, children: action === 'deleteSpeakers' ? (_jsx("p", { children: txt('deleteAllSpeakersWarning') })) : (_jsxs(_Fragment, { children: [_jsx(Form.Item, { name: "speakers", label: txt('changeSpeakersNumberTo'), children: _jsx(InputNumber, { min: 2, max: 8 }) }), _jsx(Alert, { type: "warning", message: txt('speakersChangeIrreversible'), showIcon: true })] })) }) })] }) }));
};
export default SpeakersModal;
